/**
 * Szenenmodell der Animationen.
 *
 * Eine Szene ist ein Baum aus wenigen Knotenarten. Farben werden nur über
 * Tonstufen angegeben (siehe palette.ts); die konkrete Farbe entsteht erst
 * beim Zeichnen. Ohne Angabe hat eine Form keine Füllung und eine Kontur in
 * Tinte mit der Umrissstärke.
 */
import { STROKE } from './palette.ts';
import type { Tone } from './palette.ts';

/** Darstellung einer Form. */
export interface Style {
  readonly fill?: Tone;
  readonly stroke?: Tone;
  readonly strokeWidth?: number;
  /** Nur für Ein- und Ausblenden ganzer Formen, nie für Flächenfarben. */
  readonly opacity?: number;
  readonly cap?: 'round' | 'butt' | 'square';
  readonly dash?: string;
}

/** Umformung einer Gruppe. Reihenfolge: verschieben, drehen, skalieren. */
export interface Transform {
  readonly x?: number;
  readonly y?: number;
  /** Drehung in Grad um (ox, oy). */
  readonly rotate?: number;
  /** Skalierung um (ox, oy). */
  readonly scale?: number;
  readonly scaleX?: number;
  readonly scaleY?: number;
  readonly ox?: number;
  readonly oy?: number;
}

export interface PathNode extends Style {
  readonly kind: 'path';
  readonly d: string;
}

export interface CircleNode extends Style {
  readonly kind: 'circle';
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
}

export interface EllipseNode extends Style {
  readonly kind: 'ellipse';
  readonly cx: number;
  readonly cy: number;
  readonly rx: number;
  readonly ry: number;
}

export interface RectNode extends Style {
  readonly kind: 'rect';
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly r?: number;
}

export interface LineNode extends Style {
  readonly kind: 'line';
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

export interface TextNode {
  readonly kind: 'text';
  readonly x: number;
  readonly y: number;
  readonly text: string;
  readonly size: number;
  readonly weight?: 400 | 600 | 700 | 800;
  readonly anchor?: 'start' | 'middle' | 'end';
  readonly fill?: Tone;
  readonly opacity?: number;
}

export interface GroupNode {
  readonly kind: 'group';
  readonly children: readonly SceneNode[];
  readonly transform?: Transform;
  readonly opacity?: number;
  /** Pfad, auf den der Inhalt der Gruppe beschnitten wird. */
  readonly clip?: string;
}

/** Alle Knotenarten. */
export type SceneNode = PathNode | CircleNode | EllipseNode | RectNode | LineNode | TextNode | GroupNode;

/** Eine Szene. Leere Einträge (false, null, undefined) werden übersprungen. */
export type Scene = readonly SceneNode[];

/** Eintrag, der beim Bauen einer Szene auch leer sein darf. */
export type MaybeNode = SceneNode | false | null | undefined;

/** Entfernt leere Einträge. */
export function compact(nodes: readonly MaybeNode[]): SceneNode[] {
  return nodes.filter((node): node is SceneNode => node !== false && node !== null && node !== undefined);
}

/** Gruppe mit optionaler Umformung, Deckkraft und Beschnitt. */
export function group(
  children: readonly MaybeNode[],
  options: { transform?: Transform; opacity?: number; clip?: string } = {},
): GroupNode {
  return { kind: 'group', children: compact(children), ...options };
}

/** Verschobene Gruppe. */
export function at(x: number, y: number, children: readonly MaybeNode[]): GroupNode {
  return group(children, { transform: { x, y } });
}

/** Gedrehte Gruppe um einen Drehpunkt. */
export function rotated(degrees: number, ox: number, oy: number, children: readonly MaybeNode[]): GroupNode {
  return group(children, { transform: { rotate: degrees, ox, oy } });
}

/** Ein- oder ausgeblendete Gruppe. Bei Deckkraft 0 wird nichts gezeichnet. */
export function faded(opacity: number, children: readonly MaybeNode[]): GroupNode | null {
  if (opacity <= 0.001) {
    return null;
  }
  return group(children, opacity >= 0.999 ? {} : { opacity });
}

/** Freier Pfad. */
export function shape(d: string, style: Style = {}): PathNode {
  return { kind: 'path', d, ...style };
}

/** Kreis. */
export function circle(cx: number, cy: number, r: number, style: Style = {}): CircleNode {
  return { kind: 'circle', cx, cy, r, ...style };
}

/** Ellipse. */
export function ellipse(cx: number, cy: number, rx: number, ry: number, style: Style = {}): EllipseNode {
  return { kind: 'ellipse', cx, cy, rx, ry, ...style };
}

/** Rechteck. */
export function rect(
  x: number,
  y: number,
  width: number,
  height: number,
  r = 0,
  style: Style = {},
): RectNode {
  return { kind: 'rect', x, y, width, height, r, ...style };
}

/** Linie. */
export function line(x1: number, y1: number, x2: number, y2: number, style: Style = {}): LineNode {
  return { kind: 'line', x1, y1, x2, y2, ...style };
}

/** Text in der Bildschrift. */
export function label(
  x: number,
  y: number,
  text: string,
  size: number,
  options: Partial<Omit<TextNode, 'kind' | 'x' | 'y' | 'text' | 'size'>> = {},
): TextNode {
  return { kind: 'text', x, y, text, size, weight: 700, anchor: 'middle', fill: 'ink', ...options };
}

/** Häufige Stile. */
export const styles = {
  /** Fläche mit Umriss. */
  solid: (fill: Tone): Style => ({ fill, stroke: 'ink', strokeWidth: STROKE.outline }),
  /** Fläche ohne Umriss. */
  flat: (fill: Tone): Style => ({ fill, stroke: 'none' }),
  /** Innere Linie. */
  detail: (stroke: Tone = 'ink'): Style => ({ stroke, strokeWidth: STROKE.detail }),
  /** Feine Struktur. */
  fine: (stroke: Tone = 'deep'): Style => ({ stroke, strokeWidth: STROKE.fine }),
  /** Umrisslinie ohne Füllung. */
  outline: (stroke: Tone = 'ink'): Style => ({ stroke, strokeWidth: STROKE.outline }),
  /**
   * Heller Stoff wie Eischnee, Sahne oder Mehl: in beiden Modi hell, deshalb
   * mit dunkler Kontur aus dem Innenraum-Ton statt mit Tinte.
   */
  snow: (strokeWidth: number = STROKE.detail): Style => ({ fill: 'snow', stroke: 'cavity', strokeWidth }),
} as const;
